import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Crown, Delete, Check, Coffee, LogIn, LogOut, MapPin, Settings2, CalendarClock, X, KeyRound, ShieldCheck } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/kiosk`;
const C = {
  canvas: "#FBF8F2", panel: "#FFFFFF", border: "#EFE7DA", borderSoft: "#F5EFE4",
  text: "#33221E", muted: "#8B7355", soft: "#A99483",
  burgundy: "#8B1E1E", burgundySoft: "#FBEDED", gold: "#C49A4A", goldSoft: "#FDF4E2",
  green: "#2E7D32", greenTint: "#E8F3E8", red: "#C62828", redTint: "#FBE9E9",
  purple: "#6B4EA8", purpleTint: "#EFEAF9",
};
const SLUG_KEY = "sm_kiosk_slug";
const err = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

const Key = ({ children, onClick, testid, tone = "num" }) => {
  const styles = {
    num: { background: "#fff", color: C.text, border: `1px solid ${C.border}` },
    back: { background: C.goldSoft, color: C.burgundy, border: `1px solid ${C.border}` },
    ok: { background: C.burgundy, color: "#fff", border: `1px solid ${C.burgundy}` },
  }[tone];
  return (
    <button data-testid={testid} onClick={onClick} style={styles}
      className="h-[74px] rounded-2xl font-brand font-bold text-[26px] flex items-center justify-center active:scale-95 transition-transform">
      {children}
    </button>
  );
};

export default function AttendanceKiosk() {
  const [params] = useSearchParams();
  const urlSlug = params.get("kiosk");
  const [slug, setSlug] = useState(urlSlug || localStorage.getItem(SLUG_KEY) || "");
  const [config, setConfig] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [state, setState] = useState(null);      // identified employee
  const [result, setResult] = useState(null);    // confirmation screen
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const timer = useRef(null);

  // Change-PIN modal state
  const [pinChange, setPinChange] = useState(null);  // { step: 1|2, newPin: "", confirmPin: "", error: "", busy: false }

  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    axios.get(`${BASE}/config`, { params: { kiosk: slug || undefined } })
      .then((r) => {
        setConfig(r.data);
        if (r.data.kiosk) localStorage.setItem(SLUG_KEY, r.data.kiosk.slug);
        else setSetupOpen(true);
      })
      .catch(() => setError("Kiosk is offline — check the network connection"));
  }, [slug]);

  const reset = useCallback(() => {
    setPin(""); setState(null); setResult(null); setError(""); setPinChange(null);
  }, []);

  const openPinChange = () => setPinChange({ step: 1, newPin: "", confirmPin: "", error: "", busy: false });
  const closePinChange = () => setPinChange(null);

  const pcPress = (d) => setPinChange((p) => {
    if (!p) return p;
    const field = p.step === 1 ? "newPin" : "confirmPin";
    if ((p[field] || "").length >= 4) return p;
    return { ...p, [field]: (p[field] || "") + d, error: "" };
  });
  const pcBack = () => setPinChange((p) => {
    if (!p) return p;
    const field = p.step === 1 ? "newPin" : "confirmPin";
    return { ...p, [field]: (p[field] || "").slice(0, -1), error: "" };
  });
  const pcNext = async () => {
    if (!pinChange) return;
    if (pinChange.step === 1) {
      if ((pinChange.newPin || "").length !== 4) return setPinChange({ ...pinChange, error: "Enter a 4-digit new PIN" });
      if (pinChange.newPin === pin) return setPinChange({ ...pinChange, error: "New PIN must be different from your current PIN" });
      return setPinChange({ ...pinChange, step: 2, error: "" });
    }
    if ((pinChange.confirmPin || "").length !== 4) return setPinChange({ ...pinChange, error: "Re-enter your new PIN to confirm" });
    if (pinChange.confirmPin !== pinChange.newPin) {
      return setPinChange({ ...pinChange, step: 1, newPin: "", confirmPin: "", error: "The two PINs did not match — please try again" });
    }
    setPinChange({ ...pinChange, busy: true, error: "" });
    try {
      const r = await axios.post(`${BASE}/change-pin`, {
        pin, new_pin: pinChange.newPin, kiosk: config?.kiosk?.slug,
      });
      // Success: close modal, show confirmation banner briefly, then reset to PIN screen.
      setPinChange(null);
      setResult({
        transient: false,
        action: "pin_change",
        employee: state?.employee || {},
        message: r.data?.message || "Your PIN has been updated.",
        greeting: `PIN updated, ${r.data?.employee?.first_name || state?.employee?.first_name || ""}`,
      });
      setState(null);
      setPin("");
      autoReset(7000);
    } catch (e) {
      setPinChange((p) => p && ({ ...p, busy: false, error: err(e) }));
    }
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  const autoReset = (ms = 6000) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(reset, ms);
  };

  const press = (d) => { setError(""); if (pin.length < 6) setPin(pin + d); };
  const back = () => { setError(""); setPin(pin.slice(0, -1)); };

  const submit = async () => {
    if (pin.length < 4) return setError("Enter your 4-digit PIN");
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${BASE}/identify`, { pin, kiosk: config?.kiosk?.slug });
      setState(r.data);
      autoReset(25000);
    } catch (e) { setError(err(e)); setPin(""); } finally { setBusy(false); }
  };

  const act = async (action) => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${BASE}/event`, { pin, action, kiosk: config?.kiosk?.slug });
      if (action === "break_start" || action === "break_end") {
        setState(r.data); setError(""); autoReset(25000);
        setResult({ ...r.data, transient: true });
        setTimeout(() => setResult(null), 2500);
      } else {
        setResult(r.data); setState(null); autoReset(6000);
      }
    } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };

  const clock = now.toLocaleTimeString("en-NG", { hour: "numeric", minute: "2-digit", hour12: true });
  const dateStr = now.toLocaleDateString("en-NG", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const k = config?.kiosk;

  const Photo = ({ src, name, size = 104 }) => src ? (
    <img src={src} alt={name} className="rounded-full object-cover" style={{ width: size, height: size, border: `3px solid ${C.gold}` }} />
  ) : (
    <span className="rounded-full flex items-center justify-center font-brand font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.34, background: C.goldSoft, color: C.burgundy, border: `3px solid ${C.gold}` }}>
      {(name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
    </span>
  );

  return (
    <div className="min-h-screen flex flex-col items-center" style={{ background: C.canvas }} data-testid="attendance-kiosk">
      {/* header */}
      <div className="w-full max-w-[520px] flex items-center gap-3 px-6 pt-8 pb-2">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: C.burgundy }}>
          <Crown size={23} color={C.gold} strokeWidth={2.2} />
        </div>
        <div className="leading-tight">
          <div className="font-brand font-bold text-[27px] tracking-tight" style={{ color: C.burgundy }}>Sir Monty</div>
          <div className="text-[11px] font-bold tracking-[0.18em]" style={{ color: C.soft }}>EMPLOYEE ATTENDANCE</div>
        </div>
        <div className="flex-1" />
        <button onClick={() => setSetupOpen(true)} data-testid="kiosk-setup-btn"
          className="w-10 h-10 rounded-full flex items-center justify-center border bg-white"
          style={{ borderColor: C.border, color: C.muted }}><Settings2 size={17} /></button>
      </div>

      <div className="w-full max-w-[520px] px-6 pb-10 flex-1 flex flex-col">
        {/* CONFIRMATION */}
        {result && !result.transient ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="kiosk-confirmation">
            <Photo src={result.employee?.photo_url} name={result.employee?.name} size={116} />
            <div className="font-brand font-bold text-[30px] mt-5" style={{ color: C.text }}>
              {result.greeting} <span>👋</span>
            </div>
            <div className="text-[15px] mt-1" style={{ color: C.muted }}>{result.employee?.position}</div>
            <div className="mt-6 px-6 py-4 rounded-2xl w-full"
              style={{ background: result.action === "clock_in" ? C.greenTint : C.goldSoft }}>
              <div className="font-brand font-bold text-[19px]" style={{ color: result.action === "clock_in" ? C.green : C.burgundy }}
                data-testid="kiosk-confirm-message">{result.message}</div>
              {result.action === "clock_out" && (
                <div className="text-[13.5px] mt-1" style={{ color: C.muted }}>Have a great day.</div>
              )}
              {result.action === "clock_in" && result.shift && (
                <div className="text-[13.5px] mt-1" style={{ color: C.muted }}>Today's shift · {result.shift.label}</div>
              )}
            </div>
            <button onClick={reset} data-testid="kiosk-done"
              className="mt-6 text-[13px] font-semibold" style={{ color: C.burgundy }}>Done — return to PIN screen</button>
          </div>
        ) : state ? (
          /* EMPLOYEE STATUS */
          <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="kiosk-employee">
            <Photo src={state.employee.photo_url} name={state.employee.name} />
            <div className="font-brand font-bold text-[30px] mt-5" style={{ color: C.text }} data-testid="kiosk-employee-name">
              {(() => { const h = now.getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; })()}, {state.employee.first_name} <span>👋</span>
            </div>
            <div className="text-[15px] mt-1" style={{ color: C.muted }}>
              {state.employee.position} · {state.employee.department}
            </div>

            {state.shift && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 h-10 rounded-full text-[13px] font-semibold"
                style={{ background: C.purpleTint, color: C.purple }} data-testid="kiosk-shift">
                <CalendarClock size={15} /> {state.shift.label}
              </div>
            )}

            <div className="mt-4 text-[14px]" style={{ color: C.muted }} data-testid="kiosk-status">
              {state.status === "clocked_out" && (state.clock_out
                ? `You completed your shift today (${state.clock_in_pretty} – ${state.clock_out_pretty})`
                : "You are currently clocked out")}
              {state.status === "clocked_in" && `You are clocked in since ${state.clock_in_pretty}`}
              {state.status === "on_break" && `You are on break — clocked in at ${state.clock_in_pretty}`}
            </div>

            {result?.transient && (
              <div className="mt-3 text-[13px] font-semibold" style={{ color: C.green }}>{result.message}</div>
            )}
            {error && <div className="mt-3 text-[13px] font-semibold" style={{ color: C.red }} data-testid="kiosk-error">{error}</div>}

            <div className="w-full mt-6 space-y-3">
              {state.status === "clocked_out" && !state.clock_out && (
                <button onClick={() => act("clock_in")} disabled={busy} data-testid="kiosk-clock-in"
                  className="w-full h-[68px] rounded-2xl font-brand font-bold text-[19px] tracking-wide inline-flex items-center justify-center gap-3 disabled:opacity-60"
                  style={{ background: C.burgundy, color: "#fff" }}>
                  <LogIn size={21} /> CLOCK IN
                </button>
              )}
              {(state.status === "clocked_in" || state.status === "on_break") && (
                <>
                  <button onClick={() => act("clock_out")} disabled={busy} data-testid="kiosk-clock-out"
                    className="w-full h-[68px] rounded-2xl font-brand font-bold text-[19px] tracking-wide inline-flex items-center justify-center gap-3 disabled:opacity-60"
                    style={{ background: C.burgundy, color: "#fff" }}>
                    <LogOut size={21} /> CLOCK OUT
                  </button>
                  <button onClick={() => act(state.status === "on_break" ? "break_end" : "break_start")} disabled={busy}
                    data-testid="kiosk-break"
                    className="w-full h-[56px] rounded-2xl font-semibold text-[15px] inline-flex items-center justify-center gap-2 bg-white disabled:opacity-60"
                    style={{ border: `1px solid ${C.border}`, color: C.text }}>
                    <Coffee size={18} style={{ color: C.gold }} /> {state.status === "on_break" ? "End Break" : "Start Break"}
                  </button>
                </>
              )}
              <button onClick={openPinChange} data-testid="kiosk-change-pin"
                className="w-full h-[56px] rounded-2xl font-semibold text-[15px] inline-flex items-center justify-center gap-2 bg-white disabled:opacity-60"
                style={{ border: `1px solid ${C.border}`, color: C.text }}>
                <KeyRound size={18} style={{ color: C.burgundy }} /> Change PIN
              </button>
              <button onClick={reset} data-testid="kiosk-cancel"
                className="w-full h-12 rounded-2xl text-[13.5px] font-semibold bg-transparent" style={{ color: C.soft }}>
                Not you? Back to PIN screen
              </button>
            </div>
          </div>
        ) : (
          /* PIN PAD */
          <div className="flex-1 flex flex-col justify-center">
            <div className="text-center mb-5">
              <div className="font-brand font-bold text-[24px]" style={{ color: C.text }}>Enter your PIN</div>
              <div className="text-[13px] mt-1" style={{ color: C.soft }}>Every employee has an attendance PIN</div>
            </div>

            <div className="flex items-center justify-center gap-3 mb-5" data-testid="kiosk-pin-dots">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className="w-4 h-4 rounded-full transition-colors"
                  style={{ background: pin.length > i ? C.burgundy : C.borderSoft, border: `1px solid ${C.border}` }} />
              ))}
            </div>

            {error && (
              <div className="mx-auto mb-4 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-center max-w-[420px]"
                style={{ background: C.redTint, color: C.red }} data-testid="kiosk-error">{error}</div>
            )}

            <div className="grid grid-cols-3 gap-3 max-w-[420px] w-full mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <Key key={n} testid={`kiosk-key-${n}`} onClick={() => press(String(n))}>{n}</Key>
              ))}
              <Key tone="back" testid="kiosk-key-back" onClick={back}><Delete size={24} /></Key>
              <Key testid="kiosk-key-0" onClick={() => press("0")}>0</Key>
              <Key tone="ok" testid="kiosk-key-submit" onClick={submit}><Check size={26} /></Key>
            </div>
          </div>
        )}

        {/* footer */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 text-[12.5px] font-semibold" style={{ color: C.muted }} data-testid="kiosk-footer">
            <MapPin size={14} style={{ color: C.gold }} />
            {k ? `${k.location_name} • ${k.name}` : "Kiosk not registered"}
          </div>
          <div className="text-[12.5px] mt-1 tabular-nums" style={{ color: C.soft }}>
            {dateStr} · Current time: <span style={{ color: C.text, fontWeight: 600 }}>{clock}</span>
          </div>
          <div className="mt-3 inline-flex items-center gap-1 text-[11px]" style={{ color: C.soft }} data-testid="crafted-by">
            Crafted by
            <a href="https://7thedge.ng" target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline" style={{ color: C.burgundy }}>7thEdge Solutions</a>
          </div>
        </div>
      </div>

      {/* change PIN modal */}
      {pinChange && (
        <div className="fixed inset-0 bg-black/45 flex items-center justify-center p-6 z-50" data-testid="pin-change-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" style={{ border: `1px solid ${C.border}` }}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: C.goldSoft }}>
                  <ShieldCheck size={18} style={{ color: C.burgundy }} />
                </div>
                <div>
                  <div className="font-brand font-bold text-[19px]" style={{ color: C.text }}>Change your PIN</div>
                  <div className="text-[12.5px] mt-0.5" style={{ color: C.soft }}>
                    {pinChange.step === 1 ? "Enter your new 4-digit PIN" : "Re-enter your new PIN to confirm"}
                  </div>
                </div>
              </div>
              <button onClick={closePinChange} style={{ color: C.soft }} data-testid="pin-change-close" disabled={pinChange.busy}><X size={18} /></button>
            </div>

            <div className="mt-4 flex items-center justify-center gap-2" data-testid="pin-change-dots">
              {[0, 1, 2, 3].map((i) => {
                const filled = (pinChange.step === 1 ? pinChange.newPin : pinChange.confirmPin).length > i;
                return (
                  <span key={i} className="w-3.5 h-3.5 rounded-full"
                    style={{ background: filled ? C.burgundy : C.borderSoft, border: `1px solid ${C.border}` }} />
                );
              })}
            </div>

            <div className="mt-3 flex justify-center gap-1.5 text-[11px] font-semibold tracking-wide">
              <span style={{ color: pinChange.step === 1 ? C.burgundy : C.soft }}>1. NEW PIN</span>
              <span style={{ color: C.soft }}>·</span>
              <span style={{ color: pinChange.step === 2 ? C.burgundy : C.soft }}>2. CONFIRM</span>
            </div>

            {pinChange.error && (
              <div className="mx-auto mt-3 px-3 py-2 rounded-xl text-[12.5px] font-semibold text-center"
                style={{ background: C.redTint, color: C.red }} data-testid="pin-change-error">
                {pinChange.error}
              </div>
            )}

            <div className="mt-4 grid grid-cols-3 gap-2.5">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button key={n} onClick={() => pcPress(String(n))} disabled={pinChange.busy}
                  data-testid={`pin-change-key-${n}`}
                  className="h-[58px] rounded-xl font-brand font-bold text-[22px] active:scale-95 transition-transform"
                  style={{ background: "#fff", color: C.text, border: `1px solid ${C.border}` }}>{n}</button>
              ))}
              <button onClick={pcBack} disabled={pinChange.busy} data-testid="pin-change-key-back"
                className="h-[58px] rounded-xl flex items-center justify-center active:scale-95 transition-transform"
                style={{ background: C.goldSoft, color: C.burgundy, border: `1px solid ${C.border}` }}>
                <Delete size={20} />
              </button>
              <button onClick={() => pcPress("0")} disabled={pinChange.busy} data-testid="pin-change-key-0"
                className="h-[58px] rounded-xl font-brand font-bold text-[22px] active:scale-95 transition-transform"
                style={{ background: "#fff", color: C.text, border: `1px solid ${C.border}` }}>0</button>
              <button onClick={pcNext} disabled={pinChange.busy} data-testid="pin-change-key-next"
                className="h-[58px] rounded-xl flex items-center justify-center font-brand font-bold text-[15px] active:scale-95 transition-transform disabled:opacity-60"
                style={{ background: C.burgundy, color: "#fff", border: `1px solid ${C.burgundy}` }}>
                {pinChange.busy ? "…" : pinChange.step === 1 ? "NEXT" : <Check size={22} />}
              </button>
            </div>

            <div className="mt-4 text-center text-[11.5px]" style={{ color: C.soft }}>
              Keep your new PIN private — it is the only credential used to clock in/out.
            </div>
          </div>
        </div>
      )}

      {/* device setup */}
      {setupOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50" data-testid="kiosk-setup">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" style={{ border: `1px solid ${C.border}` }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-brand font-bold text-[19px]" style={{ color: C.text }}>Kiosk setup</div>
                <div className="text-[12.5px] mt-0.5" style={{ color: C.soft }}>
                  Choose which device this is. You can also open <code>/attendance?kiosk=slug</code> directly.
                </div>
              </div>
              <button onClick={() => setSetupOpen(false)} style={{ color: C.soft }}><X size={18} /></button>
            </div>
            {config?.unknown_slug && (
              <div className="mt-3 px-3 py-2 rounded-xl text-[12.5px]" style={{ background: C.redTint, color: C.red }}>
                Unknown kiosk "{config.requested_slug}" — pick one below.
              </div>
            )}
            <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
              {(config?.kiosks || []).map((x) => (
                <button key={x.slug} data-testid={`kiosk-pick-${x.slug}`}
                  onClick={() => { localStorage.setItem(SLUG_KEY, x.slug); setSlug(x.slug); setSetupOpen(false); }}
                  className="w-full text-left px-4 py-3 rounded-xl border hover:bg-[#FBF8F2]"
                  style={{ borderColor: x.slug === k?.slug ? C.burgundy : C.border }}>
                  <div className="text-[14px] font-semibold" style={{ color: C.text }}>{x.location_name} · {x.name}</div>
                  <div className="text-[11.5px]" style={{ color: C.soft }}>{x.slug}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
